import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import OtpInput from 'react-otp-input-rc-17';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';

import { connect } from 'react-redux';
import { API } from 'Amplify';

import './styles.scss';
import Typography from '@mui/material/Typography';
import { Container, FormControl, Radio, RadioGroup } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import { groupDiagnostics } from '@lifepowr/components/src/utils/diagnostics';
import otpInputStyles from '@lifepowr/components/src/utils/otp-input-styles';
import { getFlutterWebHostPath } from '../shared/flutter-integration';
import getClientInfo from '@lifepowr/components/src/clients';

function DeviceReplace(props) {
  const { replace, setReplace } = props;
  const [claimCode, setClaimCode] = useState('');
  const [warning, setWarning] = useState(true);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const disabled = claimCode.length < 6;
  const { deviceName: thingName } = useParams();

  useEffect(() => {
    return () => {
      setClaimCode('');
      setWarning(true);
      setError(null);
      setSuccess(null);
      setLoading(false);
    };
  }, [replace]);

  const replaceDevice = async () => {
    let result;
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      result = await API.post('IO_API', `/devices/${thingName}/replace`, {
        body: { code: claimCode },
      });
      console.log(result);
      setSuccess(result);
    } catch (e) {
      setError(e?.response?.data || `${e}`);
    }
    setLoading(false);

    setTimeout(() => {}, 2000);
  };

  const codeChange = (ev) => {
    const newCode = ev.replaceAll(/[^a-zA-Z0-9]/g, '').toUpperCase();
    setClaimCode(newCode);
  };

  let buttons = (
    <DialogActions>
      <Button onClick={() => setReplace(false)}>Cancel</Button>
      <Button
        disabled={disabled}
        onClick={() => replaceDevice()}
      >
        Replace
      </Button>
    </DialogActions>
  );
  if (loading) {
    buttons = <CircularProgress />;
  } else if (success) {
    buttons = (
      <DialogActions>
        <Button onClick={() => setReplace(false)}>Close</Button>
      </DialogActions>
    );
  }

  return (
    <Dialog
      open={replace}
      onClose={() => setReplace(false)}
    >
      <DialogTitle>Replace device</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            An error ocurred: {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert severity="success">
            <AlertTitle>Success</AlertTitle>
            Success: {success}
          </Alert>
        ) : null}
        <DialogContentText>Please provide the replacement unit pairing code</DialogContentText>
        <OtpInput
          value={claimCode}
          numInputs={6}
          onChange={codeChange}
          containerStyle={otpInputStyles.container}
          inputStyle={otpInputStyles.inputStyle}
        />
        {warning ? (
          <Alert
            severity="warning"
            action={<Button onClick={() => setWarning(false)}>Understood</Button>}
          >
            <AlertTitle>Warning</AlertTitle>
            This action is irreversible. The replaced device will be rendered useless
          </Alert>
        ) : null}
      </DialogContent>
      {!warning ? buttons : null}
    </Dialog>
  );
}

function ForceUpdate({ isOpen, onClose }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedFleetName, setSelectedFleetName] = useState(null);
  const { deviceName: thingName } = useParams();

  const initialize = () => {
    setSubmitted(false);
    setError(false);
    setSelectedFleetName(null);
    fetchTemplates();
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const results = await API.get('IO_API', `/devices/${thingName}/update`);
      setTemplates(results.data);
    } catch (e) {
      setError(e?.response?.data || `${e}`);
    }
    setLoading(false);
  };

  const onSubmit = async () => {
    setError(null);
    setLoading(true);
    try {
      await API.post('IO_API', `/devices/${thingName}/update`, {
        body: { fleetName: selectedFleetName },
      });
      setSubmitted(true);
    } catch (e) {
      setError(e?.response?.data || `${e}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      initialize();
    }
  }, [isOpen]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen}
    >
      <DialogTitle>Update device</DialogTitle>
      <DialogContent>
        {/* List of templates */}
        {!error && !submitted ? (
          <DialogContentText>
            <Typography paddingBottom={2}>Select a template to update the device with:</Typography>
            <FormControl>
              <RadioGroup
                name="force-update-templates-radio-buttons-group"
                value={selectedFleetName}
                onChange={(event) => setSelectedFleetName(event.target.value)}
              >
                {templates.map((template) => (
                  <FormControlLabel
                    disabled={loading}
                    key={template}
                    value={template}
                    control={<Radio />}
                    label={template}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </DialogContentText>
        ) : null}

        {/* Success message  */}
        {submitted ? (
          <Alert severity="success">
            <AlertTitle>Success</AlertTitle>
            Your request has been submitted, a job will be create to update the device.
          </Alert>
        ) : null}

        {/* Error message */}
        {error && !submitted ? (
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            An error ocurred: {error}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <LoadingButton
          onClick={onSubmit}
          loading={loading}
          hidden={!!submitted || !!error}
        >
          <span>Submit</span>
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function DiagnosticDetails(props) {
  const { details } = props;

  const detailsStr = details;

  return (
    <div
      className="diagnosis-details"
      onClick={(ev) => ev.stopPropagation()}
    >
      <div className="diagnosis-message">
        <div>{details ? detailsStr : 'N/A'}</div>
      </div>
    </div>
  );
}

function DeviceDiagnostics(props) {
  const { diagnostics = {}, connected, admin } = props;
  const { deviceName: thingName } = useParams();
  const [active, setActive] = useState(-1);
  const [replace, setReplace] = useState(false);
  const [forceUpdateDialogVisibility, setForceUpdateDialogVisibility] = useState(false);
  const [showLegacyDeviceDiagnostics, setShowLegacyDeviceDiagnostics] = useState(false);

  const userPartnerCode = useMemo(() => {
    const clientInfo = getClientInfo();
    return clientInfo?.partnerCode;
  }, [thingName]);

  const deviceInfoPath = useMemo(() => {
    const isWL = !!userPartnerCode;

    return `${getFlutterWebHostPath(isWL)}/?tn=${thingName}`;
  }, [thingName, userPartnerCode]);

  const shouldDisplayDeprecationBanner = true;
  const diagnosticGroups = groupDiagnostics(diagnostics);

  const diagnosticGroupComponents = diagnosticGroups.map(([groupName, diagnosticsList]) => {
    const diagnosticComponents = diagnosticsList.map((diagnostic, groupIdx) => {
      const { internalName, humanName, status, details } = diagnostic;
      const idx = `${groupName}-${groupIdx}`;
      let detailsStr = details;

      if (typeof details === 'object' && details !== null) {
        detailsStr = JSON.stringify(details);
      }
      return (
        <Accordion
          key={internalName}
          expanded={active === idx}
          onClick={() => setActive((old) => (old !== idx ? idx : -1))}
        >
          <AccordionSummary>
            <div className="device-diagnostics-accordion">
              <div>{humanName}</div>

              {!(active === idx) ? <div className="device-diagnostics-accordion-details-summary">{detailsStr}</div> : null}

              <div className={status ? 'device-diagnostics-accordion-status-good' : 'device-diagnostics-accordion-status-bad'}>
                {status ? <CheckIcon /> : <ClearIcon />}
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            {active === idx ? (
              <DiagnosticDetails
                name={internalName}
                details={detailsStr}
              />
            ) : null}
          </AccordionDetails>
        </Accordion>
      );
    });

    return (
      <React.Fragment key={groupName}>
        <Typography variant="h5">{groupName}</Typography>
        <div className="diagnostics-container">{diagnosticComponents}</div>
      </React.Fragment>
    );
  });

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {connected && admin ? (
          <Button
            onClick={() => setForceUpdateDialogVisibility(true)}
            className="replace-button"
          >
            Force update
          </Button>
        ) : null}

        {!connected ? (
          <Button
            onClick={() => setReplace(true)}
            className="replace-button"
          >
            Replace device
          </Button>
        ) : null}

        {!connected ? (
          <DeviceReplace
            replace={replace}
            setReplace={setReplace}
          />
        ) : null}
        {connected && admin ? (
          <ForceUpdate
            isOpen={forceUpdateDialogVisibility}
            onClose={() => setForceUpdateDialogVisibility(false)}
          />
        ) : null}
      </Box>

      {shouldDisplayDeprecationBanner && (
        <>
          <Alert
            severity="info"
            sx={{ mb: 2, mt: 2, boxShadow: 2 }}
          >
            <AlertTitle>Deprecation Notice</AlertTitle>
            <Typography paragraph>
              This IO Diagnostic page is deprecated and all diagnostics info will be available in the new <strong>Device Info page.</strong>
            </Typography>

            <Typography component="div">
              <ul>
                <li>
                  This new page can be opened by clicking on{' '}
                  <Link
                    href={deviceInfoPath}
                    target="_blank"
                    sx={{
                      color: 'black',
                      fontWeight: 'bold',
                      ':hover': { color: 'black' },
                    }}
                  >
                    this link
                  </Link>
                  .
                </li>
                <li>
                  The old IO Diagnostic sections can still be viewed by clicking the button below, but this info is deprecated and will be removed in the near
                  future.
                </li>
              </ul>
            </Typography>
          </Alert>

          <Box sx={{ mb: 2, mt: 2 }}>
            <Button
              variant={showLegacyDeviceDiagnostics ? 'contained' : 'outlined'}
              onClick={() => {
                setShowLegacyDeviceDiagnostics(!showLegacyDeviceDiagnostics);
              }}
            >
              Click here to show/hide the deprecated “IO Diagnostic sections“
            </Button>
          </Box>
        </>
      )}

      {(showLegacyDeviceDiagnostics || !shouldDisplayDeprecationBanner) && <Box>{diagnosticGroupComponents}</Box>}
    </Container>
  );
}

const mapStateToProp = (state) => {
  const attributes = state?.system?.attributes;
  const { diagnostics, connected } = state?.system?.shadow?.reported || {};
  const system = state?.system;
  return { diagnostics, connected, attributes, shadow: system?.shadow };
};

export default connect(mapStateToProp, null, null)(DeviceDiagnostics);
